/**
 * PLA Contracts/Procurement (Agent Search) → normalized intelligence graph
 * adapter.
 *
 * Reuses the existing Agent Search request pattern
 * (`SearchApi.searchPlaContracts`, proxied via
 * `/api/agent-search/pla-contracts`) rather than inventing a new API
 * contract, and interprets procurement fields semantically:
 * - tenderCompany     → buyer/tendering organization (PROCURED)
 * - bidUnit           → bidder/winning supplier (SUPPLIED / BID_ON)
 * - agencyCompany     → procurement/tender agency (BROKERED_BY)
 * - tenderContactName → buyer/tender contact (CONTACT_FOR)
 * - agencyContactName → agency contact (CONTACT_FOR)
 * - emails            → email/domain intelligence (USES_EMAIL/USES_DOMAIN)
 * - title/content     → procurement evidence + derived technology concepts
 *
 * Agent Search can return semantically-related records that never mention
 * the searched entity, so entity searches are strict-filtered to records
 * that literally mention the query (exact-match flag or literal text match).
 */
import {
  AgentSearchResult,
  SearchApi as AgentSearchApi,
} from '../../api/agentSearch/search/SearchApi';
import { IntelEvidence } from '../../types/intelligenceGraph';
import { IntelligenceGraphBuilder } from './graphBuilder';
import {
  looksLikePlaOrganization,
  makeNodeId,
  normalizeEntityName,
} from './entityResolver';

/** Agent Search caps pageSize; expansion default per the workflow spec. */
export const DEFAULT_PROCUREMENT_PAGE_SIZE = 100;

const MAX_EMAILS_PER_RECORD = 10;
const MAX_DERIVED_TECH_PER_RECORD = 6;

/**
 * Seed patterns used to derive candidate technology concepts from
 * procurement `title`/`content`. Extraction is heuristic NLP, so every
 * resulting edge is marked `derived` and rendered dashed with a confidence
 * indicator — never presented as an observed relationship.
 */
const TECHNOLOGY_PATTERNS: RegExp[] = [
  /相控阵(?:天线|雷达)?/g,
  /(?:有源|无源|气象|激光|毫米波)?雷达(?:系统|整机)?/g,
  /T\/R\s*组件/g,
  /(?:微波|射频|毫米波)(?:组件|模块|器件|电路)/g,
  /(?:混合|多层|陶瓷|高频)?基板/g,
  /立体装配(?:设备)?/g,
  /(?:半导体|集成电路|芯片)(?:测试|封装|制造)?(?:设备|系统)?/g,
  /(?:卫星|导航|通信|数据链)(?:终端|系统|设备)/g,
  /无人(?:机|艇|车)(?:系统|平台)?/g,
  /(?:测试|测量|校准|试验)(?:系统|设备|平台)/g,
  /(?:仿真|模拟)(?:系统|平台|器)/g,
  /(?:电源|供电)(?:模块|系统|设备)/g,
  /天线(?:阵列|罩|系统)?/g,
  /(?:信号处理|数据处理)(?:机|板卡|系统)/g,
  /(?:光电|红外|光学)(?:系统|设备|器件)/g,
];

export interface ProcurementSummaryFields {
  tenderCompany?: string;
  tenderCompanyId?: string;
  bidUnit?: string;
  bidUnitId?: string;
  agencyCompany?: string;
  agencyCompanyId?: string;
  bidAmount?: string;
  tenderPrice?: string;
  tenderCode?: string;
  tenderContact?: string;
  tenderContactName?: string;
  tenderContactPhone?: string;
  agencyContact?: string;
  agencyContactName?: string;
  agencyContactPhone?: string;
}

export interface ProcurementGraphRecord {
  id: string;
  title: string;
  content: string;
  createTime?: string;
  tenderCategoryName?: string;
  areaName?: string;
  emails: string[];
  summary: ProcurementSummaryFields;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asEmailArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => asString(v))
      .filter((email): email is string => Boolean(email));
  }
  const single = asString(value);
  return single ? [single] : [];
}

/**
 * Maps a raw Agent Search result to a typed procurement record, tolerating
 * both `structData.data.*` nesting and flat `structData.*` layouts, and the
 * `emails` array living as a sibling of `summary` (the common shape) or
 * nested inside it.
 */
export function mapAgentSearchResultToProcurementGraphRecord(
  result: AgentSearchResult,
): ProcurementGraphRecord {
  const structData = (result.document?.structData || {}) as Record<
    string,
    unknown
  >;
  const data = (
    structData.data && typeof structData.data === 'object'
      ? structData.data
      : structData
  ) as Record<string, unknown>;
  const summary = (
    data.summary && typeof data.summary === 'object' ? data.summary : {}
  ) as Record<string, unknown>;

  const title =
    asString((result.document?.derivedStructData || {}).title) ||
    asString(data.title) ||
    asString(result.document?.id) ||
    asString(result.id) ||
    'Untitled procurement record';

  return {
    id:
      asString(data.id) ||
      asString(result.document?.id) ||
      asString(result.id) ||
      title,
    title,
    content: asString(data.content) || title,
    createTime: asString(data.createTime),
    tenderCategoryName: asString(data.tenderCategoryName),
    areaName: asString(data.areaName),
    emails: asEmailArray(data.emails).length
      ? asEmailArray(data.emails)
      : asEmailArray(summary.emails),
    summary: {
      tenderCompany: asString(summary.tenderCompany),
      tenderCompanyId: asString(summary.tenderCompanyId),
      bidUnit: asString(summary.bidUnit),
      bidUnitId: asString(summary.bidUnitId),
      agencyCompany: asString(summary.agencyCompany),
      agencyCompanyId: asString(summary.agencyCompanyId),
      bidAmount: asString(summary.bidAmount),
      tenderPrice: asString(summary.tenderPrice),
      tenderCode: asString(summary.tenderCode),
      tenderContact: asString(summary.tenderContact),
      tenderContactName: asString(summary.tenderContactName),
      tenderContactPhone: asString(summary.tenderContactPhone),
      agencyContact: asString(summary.agencyContact),
      agencyContactName: asString(summary.agencyContactName),
      agencyContactPhone: asString(summary.agencyContactPhone),
    },
  };
}

/**
 * True when the record literally mentions the searched entity in a company
 * field, title, or content. Filters out semantic-only Agent Search results
 * so an organization search only fuses records that strictly mention it.
 */
export function recordStrictlyMentions(
  record: ProcurementGraphRecord,
  term: string,
): boolean {
  const normalizedTerm = normalizeEntityName(term);
  if (!normalizedTerm) return true;
  const haystacks = [
    record.summary.tenderCompany,
    record.summary.bidUnit,
    record.summary.agencyCompany,
    record.title,
    record.content,
  ];
  return haystacks.some(
    (value) => value && normalizeEntityName(value).includes(normalizedTerm),
  );
}

/** Derives candidate technology concepts from procurement title/content. */
export function extractProcurementTechnologies(
  record: ProcurementGraphRecord,
): string[] {
  const text = `${record.title}\n${record.content.slice(0, 4000)}`;
  const found = new Set<string>();
  TECHNOLOGY_PATTERNS.forEach((pattern) => {
    const matches = text.match(pattern);
    matches?.forEach((match) => {
      const cleaned = normalizeEntityName(match);
      if (cleaned.length >= 2) found.add(cleaned);
    });
  });
  return Array.from(found).slice(0, MAX_DERIVED_TECH_PER_RECORD);
}

function procurementEvidence(record: ProcurementGraphRecord): IntelEvidence {
  return {
    sourceDataset: 'pla-contracts',
    sourceRecordId: record.id,
    evidence: record.title,
    detail: {
      buyer: record.summary.tenderCompany,
      supplier: record.summary.bidUnit,
      agency: record.summary.agencyCompany,
      amount: record.summary.bidAmount || record.summary.tenderPrice,
      tenderCode: record.summary.tenderCode,
      createTime: record.createTime,
      category: record.tenderCategoryName,
      area: record.areaName,
    },
  };
}

/**
 * Root/search entity context threaded through procurement graph
 * construction so explicitly-mentioned records always connect back to the
 * searched organization (item: no orphaned procurement nodes).
 */
export interface ProcurementRootContext {
  /** Graph node id of the root/search organization. */
  rootId: string;
  /** Raw root organization name/label. */
  rootName: string;
}

function mentions(value: string | undefined, normalizedTerm: string): boolean {
  return Boolean(value && normalizeEntityName(value).includes(normalizedTerm));
}

/**
 * Resolves an organization-role node id, preferring a previously-registered
 * upstream company id so name variants of the same company fuse onto one
 * node even when normalized names differ.
 */
function resolveOrgNodeId(
  builder: IntelligenceGraphBuilder,
  type: 'organization' | 'pla' | 'buyer' | 'supplier' | 'agency',
  name: string,
  companyId?: string,
): string {
  const externalKey = companyId ? `pla-company:${companyId}` : undefined;
  if (externalKey) {
    const known = builder.resolveExternalId(externalKey);
    if (known) return known;
  }
  const id = makeNodeId(type, name);
  if (externalKey) builder.registerExternalId(externalKey, id);
  return id;
}

/**
 * Adds one procurement record and all of its observed/derived relationships
 * to the builder.
 */
export function addProcurementToGraph(
  builder: IntelligenceGraphBuilder,
  record: ProcurementGraphRecord,
  root?: ProcurementRootContext,
): void {
  const evidence = procurementEvidence(record);
  const procurement = builder.upsertNode({
    type: 'procurement',
    label: record.title,
    id: `procurement:${record.id}`,
    dataset: 'pla-contracts',
    evidence,
    meta: {
      recordId: record.id,
      buyer: record.summary.tenderCompany,
      supplier: record.summary.bidUnit,
      agency: record.summary.agencyCompany,
      amount: record.summary.bidAmount || record.summary.tenderPrice,
      tenderCode: record.summary.tenderCode,
      createTime: record.createTime,
      category: record.tenderCategoryName,
      area: record.areaName,
      buyerContact:
        record.summary.tenderContactName || record.summary.tenderContact,
      buyerContactPhone: record.summary.tenderContactPhone,
      agencyContact:
        record.summary.agencyContactName || record.summary.agencyContact,
      agencyContactPhone: record.summary.agencyContactPhone,
      emails: record.emails.length ? record.emails.join(', ') : undefined,
      // Full procurement text for the record viewer — the Agent Search
      // response already returns it, so no second API request is needed.
      content: record.content,
    },
  });

  const commonEdge = {
    sourceDataset: 'pla-contracts' as const,
    sourceRecordId: record.id,
    evidence: record.title,
  };

  // --- Buyer / tendering organization ---
  const { tenderCompany, bidUnit, agencyCompany } = record.summary;
  let buyerId: string | undefined;
  if (tenderCompany) {
    const pla = looksLikePlaOrganization(normalizeEntityName(tenderCompany))
      ? 'PLA/military name pattern (procurement buyer)'
      : undefined;
    const buyerType = pla ? 'pla' : 'buyer';
    const buyer = builder.upsertNode({
      type: buyerType,
      id: resolveOrgNodeId(
        builder,
        buyerType,
        tenderCompany,
        record.summary.tenderCompanyId,
      ),
      label: tenderCompany,
      dataset: 'pla-contracts',
      evidence,
      plaEvidence: pla,
    });
    buyerId = buyer.id;
    builder.upsertEdge({
      ...commonEdge,
      source: buyer.id,
      target: procurement.id,
      type: 'PROCURED',
      relationshipType: 'observed',
      confidence: 1,
    });
  }

  // --- Supplier / winning bidder ---
  let supplierId: string | undefined;
  if (bidUnit) {
    const supplier = builder.upsertNode({
      type: 'supplier',
      id: resolveOrgNodeId(
        builder,
        'supplier',
        bidUnit,
        record.summary.bidUnitId,
      ),
      label: bidUnit,
      dataset: 'pla-contracts',
      evidence,
    });
    supplierId = supplier.id;
    builder.upsertEdge({
      ...commonEdge,
      source: supplier.id,
      target: procurement.id,
      type: 'SUPPLIED',
      relationshipType: 'observed',
      confidence: 1,
    });
  }

  // --- Procurement agency ---
  let agencyId: string | undefined;
  if (agencyCompany) {
    const agency = builder.upsertNode({
      type: 'agency',
      id: resolveOrgNodeId(
        builder,
        'agency',
        agencyCompany,
        record.summary.agencyCompanyId,
      ),
      label: agencyCompany,
      dataset: 'pla-contracts',
      evidence,
      meta: {
        contact:
          record.summary.agencyContactName || record.summary.agencyContact,
        contactPhone: record.summary.agencyContactPhone,
      },
    });
    agencyId = agency.id;
    builder.upsertEdge({
      ...commonEdge,
      source: procurement.id,
      target: agency.id,
      type: 'BROKERED_BY',
      relationshipType: 'observed',
      confidence: 1,
    });
  }

  // --- Contacts ---
  const contacts: {
    name?: string;
    orgLabel?: string;
    orgId?: string;
    role: string;
  }[] = [
    {
      name: record.summary.tenderContactName || record.summary.tenderContact,
      orgLabel: tenderCompany,
      orgId: buyerId,
      role: 'Buyer/tender contact',
    },
    {
      name: record.summary.agencyContactName || record.summary.agencyContact,
      orgLabel: agencyCompany,
      orgId: agencyId,
      role: 'Agency contact',
    },
  ];
  contacts.forEach(({ name, orgLabel, orgId, role }) => {
    const cleaned = name ? normalizeEntityName(name) : '';
    if (!cleaned || cleaned.length > 20) return;
    const person = builder.upsertNode({
      type: 'person',
      label: cleaned,
      dataset: 'pla-contracts',
      evidence,
      meta: { role },
    });
    builder.upsertEdge({
      ...commonEdge,
      source: person.id,
      target: procurement.id,
      type: 'CONTACT_FOR',
      relationshipType: 'observed',
      confidence: 1,
    });
    // Reuse the already-created buyer/agency node id so the affiliation edge
    // never re-types an agency node into a generic organization.
    if (orgId || orgLabel) {
      builder.upsertEdge({
        ...commonEdge,
        source: person.id,
        target: orgId ?? makeNodeId('organization', orgLabel as string),
        type: 'AFFILIATED_WITH',
        relationshipType: 'derived',
        confidence: 0.8,
      });
    }
  });

  // --- Email / domain intelligence ---
  record.emails.slice(0, MAX_EMAILS_PER_RECORD).forEach((email) => {
    const cleaned = normalizeEntityName(email).toLowerCase();
    if (!cleaned.includes('@')) return;
    const emailNode = builder.upsertNode({
      type: 'email',
      label: cleaned,
      dataset: 'pla-contracts',
      evidence,
    });
    const owner = buyerId ?? procurement.id;
    builder.upsertEdge({
      ...commonEdge,
      source: owner,
      target: emailNode.id,
      type: 'USES_EMAIL',
      relationshipType: 'observed',
      confidence: 1,
    });
    const domain = cleaned.split('@')[1];
    if (domain) {
      const domainNode = builder.upsertNode({
        type: 'domain',
        label: domain,
        dataset: 'pla-contracts',
        evidence,
      });
      builder.upsertEdge({
        ...commonEdge,
        source: emailNode.id,
        target: domainNode.id,
        type: 'USES_DOMAIN',
        relationshipType: 'observed',
        confidence: 1,
      });
    }
  });

  // --- Derived technology concepts (NLP extraction, clearly derived) ---
  extractProcurementTechnologies(record).forEach((technology) => {
    const tech = builder.upsertNode({
      type: 'technology',
      label: technology,
      dataset: 'pla-contracts',
      evidence,
    });
    builder.upsertEdge({
      ...commonEdge,
      source: procurement.id,
      target: tech.id,
      type: 'RELATED_TECHNOLOGY',
      relationshipType: 'derived',
      confidence: 0.5,
    });
  });

  // --- Root/search entity linkage guarantee ---
  // A record was fused because it explicitly mentions the searched
  // organization; when the summary-field edges above did not already connect
  // the record to the root node (e.g. name variants, missing tenderCompany,
  // mention only in title/content), link it here so explicitly-sourced
  // procurement records are never orphaned from the search entity.
  if (root && root.rootId !== procurement.id) {
    const alreadyLinked = [buyerId, supplierId, agencyId].includes(root.rootId);
    if (!alreadyLinked) {
      const normalizedRoot = normalizeEntityName(root.rootName);
      const buyerMentionsRoot = mentions(tenderCompany, normalizedRoot);
      const recordMentionsRoot =
        buyerMentionsRoot ||
        mentions(bidUnit, normalizedRoot) ||
        mentions(agencyCompany, normalizedRoot) ||
        mentions(record.title, normalizedRoot) ||
        mentions(record.content, normalizedRoot);
      if (recordMentionsRoot) {
        if (mentions(bidUnit, normalizedRoot)) {
          builder.upsertEdge({
            ...commonEdge,
            source: root.rootId,
            target: procurement.id,
            type: 'SUPPLIED',
            relationshipType: 'observed',
            confidence: 1,
          });
        } else if (mentions(agencyCompany, normalizedRoot)) {
          builder.upsertEdge({
            ...commonEdge,
            source: procurement.id,
            target: root.rootId,
            type: 'BROKERED_BY',
            relationshipType: 'observed',
            confidence: 1,
          });
        } else {
          // Buyer-name variant or title/content mention → the organization
          // procured (observed when named as buyer, derived for text mention).
          builder.upsertEdge({
            ...commonEdge,
            source: root.rootId,
            target: procurement.id,
            type: buyerMentionsRoot ? 'PROCURED' : 'MENTIONED_IN',
            relationshipType: buyerMentionsRoot ? 'observed' : 'derived',
            confidence: buyerMentionsRoot ? 1 : 0.7,
          });
        }
      } else if (!buyerId && !supplierId && !agencyId) {
        // eslint-disable-next-line no-console
        console.warn(
          '[GraphIntegrity] Orphan procurement detected:',
          `recordId=${record.id}`,
          `tenderCompany=${tenderCompany ?? '(none)'}`,
        );
      }
    }
  }
}

/**
 * Searches PLA Contracts for an entity/term and merges strictly-matching
 * records into the builder. Returns counts so the UI can offer "load more".
 */
export async function searchProcurementIntoGraph(
  builder: IntelligenceGraphBuilder,
  query: string,
  size: number = DEFAULT_PROCUREMENT_PAGE_SIZE,
  pageToken: string | null = null,
  strict = true,
  root?: ProcurementRootContext,
): Promise<{ processed: number; total: number; nextPageToken?: string }> {
  const response = await AgentSearchApi.searchPlaContracts(
    query,
    size,
    pageToken,
  );
  const results = response.data.results ?? [];
  const records = results
    .map(mapAgentSearchResultToProcurementGraphRecord)
    .filter((record) => !strict || recordStrictlyMentions(record, query));
  records.forEach((record) => addProcurementToGraph(builder, record, root));
  return {
    processed: records.length,
    total: response.data.totalSize ?? records.length,
    nextPageToken: response.data.nextPageToken,
  };
}
